import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Navigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Download, Search, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';
const q = (v) => `Q${Number(v || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function Field({ label, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">{label}</Label>
      {children}
    </div>
  );
}

function PickSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? '' : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function PivotTable({ data }) {
  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-x-auto">
      <table className="w-full text-sm" data-testid="compras-table">
        <thead className="bg-[#2c57c5] text-white text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2 sticky left-0 bg-[#2c57c5]">Proveedor</th>
            {data.meses.map(m => <th key={m.key} className="text-right px-3 py-2 whitespace-nowrap">{m.label}</th>)}
            <th className="text-right px-3 py-2">Total</th>
            <th className="text-right px-3 py-2"># Pagos</th>
          </tr>
        </thead>
        <tbody>
          {data.proveedores.map((p, i) => (
            <tr key={p.proveedor_id || p.nit || i} className="border-t border-slate-100 dark:border-slate-800" data-testid={`compras-row-${i}`}>
              <td className="px-3 py-2 sticky left-0 bg-white dark:bg-slate-900">
                <p className="font-medium text-slate-800 dark:text-slate-100">{p.nombre}</p>
                <p className="text-[11px] text-slate-500">{p.nit}</p>
              </td>
              {data.meses.map(m => (
                <td key={m.key} className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {p.por_mes[m.key] ? q(p.por_mes[m.key]) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900 dark:text-slate-100">{q(p.total)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{p.cantidad}</td>
            </tr>
          ))}
          {data.proveedores.length === 0 && (
            <tr><td colSpan={data.meses.length + 3} className="text-center py-10 text-slate-500">Sin compras con estos filtros</td></tr>
          )}
        </tbody>
        {data.proveedores.length > 0 && (
          <tfoot className="bg-blue-50 dark:bg-slate-800 font-bold">
            <tr>
              <td className="px-3 py-2 sticky left-0 bg-blue-50 dark:bg-slate-800">TOTAL</td>
              {data.meses.map(m => <td key={m.key} className="px-3 py-2 text-right tabular-nums">{q(m.total)}</td>)}
              <td className="px-3 py-2 text-right tabular-nums" data-testid="compras-total">{q(data.total)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{data.cantidad}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </Card>
  );
}

export default function ComprasProveedorReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const has = (p) => user?.role === 'admin' || (user?.permissions || []).includes(p);
  const canDownload = has('descargar_reporte_compras_proveedor');
  const [stores, setStores] = useState([]);
  const [providers, setProviders] = useState([]);
  const [f, setF] = useState({ fecha_inicio: yearStart(), fecha_fin: today(), tienda_id: '', proveedor_id: '', tipo_pago: '', monto_min: '', monto_max: '', total_min: '', total_max: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const set = (k) => (v) => setF(prev => ({ ...prev, [k]: v }));

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v !== '' && v !== null) p.append(k, v); });
    return p.toString();
  }, [f]);

  const search = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/reports/compras-proveedor?${params()}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cargar el reporte'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/stores`).then(r => setStores(r.data || [])).catch(() => {});
    axios.get(`${API}/providers`).then(r => setProviders(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { search(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/compras-proveedor/export?${params()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compras_proveedor_${f.fecha_inicio || 'inicio'}_${f.fecha_fin || 'hoy'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) { toast.error('No se pudo descargar'); } finally { setDownloading(false); }
  };

  if (user && !has('ver_reporte_compras_proveedor')) return <Navigate to="/dashboard" />;

  const num = (k, ph, tid) => <Input type="number" inputMode="decimal" min="0" value={f[k]} onChange={(e) => set(k)(e.target.value)} placeholder={ph} data-testid={tid} />;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="compras-proveedor-page">
      <div className="container mx-auto px-4 py-4 space-y-5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="-ml-2" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver al Dashboard
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-[#2c57c5]" /> Compras por Proveedor
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Solo pagos aprobados, separados por mes (hora de Guatemala).</p>
          </div>
          {canDownload && (
            <Button onClick={download} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="compras-download-btn">
              <Download className="h-4 w-4 mr-2" /> {downloading ? 'Generando…' : 'Descargar Excel'}
            </Button>
          )}
        </div>

        <Card className="p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Desde"><Input type="date" value={f.fecha_inicio} onChange={(e) => set('fecha_inicio')(e.target.value)} data-testid="compras-desde" /></Field>
            <Field label="Hasta"><Input type="date" value={f.fecha_fin} onChange={(e) => set('fecha_fin')(e.target.value)} data-testid="compras-hasta" /></Field>
            <Field label="Tienda">
              <PickSelect value={f.tienda_id} onChange={set('tienda_id')} placeholder="Todas las tiendas" testid="compras-tienda"
                options={stores.map(s => ({ value: s.id, label: s.nombre }))} />
            </Field>
            <Field label="Proveedor">
              <PickSelect value={f.proveedor_id} onChange={set('proveedor_id')} placeholder="Todos los proveedores" testid="compras-proveedor"
                options={providers.map(p => ({ value: p.id, label: `${p.nombre} · ${p.nit}` }))} />
            </Field>
            <Field label="Condición">
              <PickSelect value={f.tipo_pago} onChange={set('tipo_pago')} placeholder="Contado y Crédito" testid="compras-condicion"
                options={[{ value: 'contado', label: 'Contado' }, { value: 'credito', label: 'Crédito' }]} />
            </Field>
            <Field label="Monto por pago (Q)">
              <div className="flex gap-2">{num('monto_min', 'Mín.', 'compras-monto-min')}{num('monto_max', 'Máx.', 'compras-monto-max')}</div>
            </Field>
            <Field label="Total por proveedor (Q)">
              <div className="flex gap-2">{num('total_min', 'Mín.', 'compras-total-min')}{num('total_max', 'Máx.', 'compras-total-max')}</div>
            </Field>
            <div className="flex items-end">
              <Button onClick={search} disabled={loading} className="w-full bg-[#2c57c5] hover:bg-[#1e3a8a] text-white" data-testid="compras-search-btn">
                <Search className="h-4 w-4 mr-2" /> {loading ? 'Buscando…' : 'Buscar'}
              </Button>
            </div>
          </div>
        </Card>

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"><p className="text-xs text-slate-500">Total comprado</p><p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100" data-testid="compras-kpi-total">{q(data.total)}</p></Card>
              <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"><p className="text-xs text-slate-500">Proveedores</p><p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="compras-kpi-proveedores">{data.proveedores.length}</p></Card>
              <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"><p className="text-xs text-slate-500">Pagos aprobados</p><p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="compras-kpi-pagos">{data.cantidad}</p></Card>
            </div>
            <PivotTable data={data} />
            {canDownload && <p className="text-xs text-slate-500 dark:text-slate-400">El Excel incluye: hoja Resumen (proveedor × mes), una hoja por cada mes con sus proveedores y totales, y hoja Detalle con cada pago agrupado por mes y subtotales.</p>}
          </>
        )}
      </div>
    </div>
  );
}
